import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import "../../styles/Form.css";
import logo from "../../images/logo.png";

const PadlockProbePolicy = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [username, setUsername] = useState("");
  const userId = localStorage.getItem("userId");
  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");
  const [checkboxStates, setCheckboxStates] = useState({
    padlock: false,
    temp: false,
  });

  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const effectiveUserId = isAdmin && paramId ? paramId : userId;

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${effectiveUserId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { first_name, last_name } = res.data;
        setUsername(`${first_name} ${last_name}`);
      } catch (err) {
        console.error("Error fetching user name:", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const savedAnswers = res.data.answers?.padlock_temperature_probe_policy;

        if (savedAnswers) {
          setCheckboxStates({
            padlock: Number(savedAnswers.ut_q1) === 1,
            temp: Number(savedAnswers.ut_q2) === 1,
          });
        }

        const TASK_ID = 15; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "padlock_temperature_probe_policy";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        const signed = status === "review" || status === "completed";

        if (signed) {
          setIsSigned(true);
          setSignedDate(
            res.data.signdates?.[idKey] ?? res.data.signdates?.[nameKey]
          );
        }
      } catch (err) {
        console.error("Error checking task status:", err);
      }
    };

    fetchUserName();
    fetchTaskStatus();
  }, [API_BASE, userId, isAdmin, effectiveUserId]);

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Padlock/Temperature Probe Policy
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>
      <div
        className="form-wrapper my-4 print-area"
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: "150px", height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Padlock/Temperature Probe Policy</h2>
          </div>
          <CompanyAddress />
        </div>

        <h5 className="my-5 text-center">Padlock Agreement</h5>
        <p>
          All Company Drivers and Owner/Operators must use padlock to secure
          trailers, regardless if trailer is loaded, empty, Sealed or Not Sealed
          (No Exemptions).
        </p>
        <p>
          Pad Locks are supplied by the company during Onboarding, if padlock is
          not returned upon exiting company or if lost there will be deduction
          of $25/ from your pay cheque.
        </p>

        {/* ✅ Updated Checkbox */}
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="padlockCheck"
            checked={checkboxStates.padlock}
            onChange={() =>
              setCheckboxStates((prev) => ({ ...prev, padlock: !prev.padlock }))
            }
            disabled={!isAdmin && isSigned}
          />
          <label className="form-check-label" htmlFor="padlockCheck">
            I have read this policy
          </label>
        </div>
        <hr />

        <h5 className="my-5 text-center">Temperature Probe</h5>
        <p className="mb-5">
          This form indicates that you as a company driver and owner-operator,
          are supplied with the Temperature Probe from New Way Transport
          during On-boarding. The cost for the probe will be deducted from the
          first pay cheque i.e. $25 and reimbursed upon returning the
          Temperature Probe when exiting the company during end of employment.
        </p>
        <p>
          If at any point you lose the Temperature Probe or it is misplaced, you
          must buy a probe by yourself. All of the Walmart/Reefer loads require
          this Temperature Probe, if you are found without the Temperature probe
          and there is a claim you will be held responsible.
        </p>
        {/* ✅ Updated Checkbox */}
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="tempCheck"
            checked={checkboxStates.temp}
            onChange={() =>
              setCheckboxStates((prev) => ({ ...prev, temp: !prev.temp }))
            }
            disabled={!isAdmin && isSigned}
          />
          <label className="form-check-label" htmlFor="tempCheck">
            I have read this policy
          </label>
        </div>
        <hr />

        <h5 className="my-5 text-center"> Acknowledgment</h5>
        <p>
          I <strong>{username}</strong>, have read the above policies and
          understand that I am responsible if either item is lost or damaged.{" "}
        </p>

        <hr className="my-5" />

        <form>
          <div className="row mb-3 mt-5">
            <div className="col">
              <label className="form-label">Employee Name</label>
              <input
                type="text"
                className="form-control"
                value={username}
                readOnly
              />
            </div>
            <div className="col">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={
                  isSigned && signedDate
                    ? new Date(signedDate).toLocaleDateString()
                    : new Date().toLocaleDateString()
                }
                readOnly
              />
            </div>
          </div>
        </form>

        {userId && (
          <>
            <SignatureManager
              taskName="padlock_temperature_probe_policy"
              taskId={15}
              userId={effectiveUserId}
              isAdmin={isAdmin}
              extraAnswers={{
                ut_q1: checkboxStates.padlock ? 1 : 0,
                ut_q2: checkboxStates.temp ? 1 : 0,
              }}
            />
            <ReviewManager
              taskName="padlock_temperature_probe_policy"
              taskId={15}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default PadlockProbePolicy;
